import {
    forwardRef,
    useEffect,
    useMemo,
    useRef,
    useId
} from "react";
import {
    type PressableProps,
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IOptionRowProps from "./type";
import type {
    OptionRowKeyboardEventType,
    OptionRowMouseEventType,
    OptionRowWebPropsType,
    IOptionRowRef
} from "./type";
import stylesheet, {
    OPTION_ROW_DESCRIPTION_VARIANT,
    OPTION_ROW_SHORTCUT_VARIANT,
    OPTION_ROW_LABEL_VARIANT,
    OPTION_ROW_META_VARIANT,
    getOptionRowMeasures,
    OPTION_ROW_SPACE_KEY,
    getOptionRowColors,
    getOptionRowRadius,
    useStyles
} from "./stylesheet";
import OptionRowHighlight from "./components/highlight";
import {
    getOptionRowText
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    useInteractionState
} from "../../helpers/interaction";
import {
    getAppearStyle,
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    RefForwardingComponent
} from "../../types";
import {
    Check as CheckIcon
} from "lucide-react-native";
import {
    getItemTitleVariant
} from "../item/stylesheet";
import StateLayer from "../stateLayer";
import CheckBox from "../checkBox";
import Text from "../text";

const preventFocusSteal = (event: OptionRowMouseEventType) => {
    event.preventDefault();
};

const OptionRow: RefForwardingComponent<IOptionRowRef, IOptionRowProps> = ({
    isFocusPreserved: isFocusPreservedProp,
    "aria-describedby": ariaDescribedBy,
    "aria-selected": ariaSelected,
    isShowCheck: isShowCheckProp,
    "aria-checked": ariaChecked,
    isHoverSuppressed = false,
    isReserveLeading = false,
    icon: IconComponentProp,
    isShowActiveRing = true,
    variant = "popover",
    isDisabled = false,
    isMultiple = false,
    isSelected = false,
    onHighlightChange,
    isActive = false,
    type = "neutral",
    role = "option",
    tabIndex = -1,
    customTheme,
    description,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    shortcut,
    trailing,
    leading,
    onFocus,
    onPress,
    onBlur,
    label,
    style,
    meta,
    ...props
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const baseId = useId();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isWeb = Platform.OS === "web";

    const descriptionId = `${baseId}-description`;

    const isDescriptionVisible = !!description;

    const isMetaVisible = !!meta;

    const isShowCheck = isShowCheckProp ?? (role === "option" && !isMultiple);

    const isFocusPreserved = isFocusPreservedProp ?? role === "option";

    const isCheckVisible = isShowCheck && isSelected;

    const isSelectedToneVisible = isSelected && !isMultiple && role !== "menuitemcheckbox" && !isDisabled;

    const isActiveToneVisible = isActive && !isSelectedToneVisible && !isDisabled;

    const isLayerHovered = !isDisabled && ((isHovered && !isHoverSuppressed) || isFocusVisible);

    const isLayerPressed = !isDisabled && isPressed;

    const isFocusRingVisible = (isActive && isShowActiveRing) || isFocusVisible;

    const isHighlighted = isLayerHovered || isLayerPressed || isSelectedToneVisible || isActive || isFocusVisible;

    const isSpaceKeyHandled = isWeb && !!onPress && !isDisabled && role !== "option" && role !== "button";

    const labelText = getOptionRowText({
        node: label
    });

    const currentMeasures = getOptionRowMeasures({
        isDescriptionVisible,
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        variant,
        spaces
    });

    const currentRadius = getOptionRowRadius({
        minHeight: currentMeasures.minHeight,
        radiuses,
        variant,
        borders,
        spaces
    });

    const currentColors = getOptionRowColors({
        isLayered: isLayerHovered || isLayerPressed || isActiveToneVisible,
        isSelectedToneVisible,
        isActiveToneVisible,
        isDisabled,
        colors,
        type
    });

    const currentLabelVariant = getItemTitleVariant({
        titleVariant: OPTION_ROW_LABEL_VARIANT,
        isSelected: isSelectedToneVisible,
        size: "medium"
    });

    const {
        textColumn: textColumnDynamicStyle,
        container: containerDynamicStyle,
        iconSlot: iconSlotDynamicStyle,
        shortcut: shortcutDynamicStyle,
        trailing: trailingDynamicStyle
    } = useStyles({
        borderRadius: currentRadius,
        isFocusRingVisible,
        currentMeasures,
        currentColors,
        isMetaVisible,
        shapeConfigs,
        isDisabled,
        borders,
        colors,
        spaces,
        type
    });

    const {
        isRendered: isCheckRendered,
        progress: checkProgress,
        isReduceMotionEnabled
    } = usePresence({
        enterIntent: "iconAppear",
        isVisible: isCheckVisible,
        exitIntent: "iconAppear",
        customTheme
    });

    const checkAppearStyle = useMemo(() => getAppearStyle({
        progress: checkProgress,
        isReduceMotionEnabled
    }), [
        isReduceMotionEnabled,
        checkProgress
    ]);

    const onHighlightChangeRef = useRef(onHighlightChange);

    useEffect(() => {
        onHighlightChangeRef.current = onHighlightChange;
    });

    useEffect(() => {
        onHighlightChangeRef.current?.(isHighlighted);
    }, [
        isHighlighted
    ]);

    useEffect(() => {
        return () => {
            onHighlightChangeRef.current?.(false);
        };
    }, []);

    const onKeyDown = (event: OptionRowKeyboardEventType) => {
        if(event.key !== OPTION_ROW_SPACE_KEY || event.target !== event.currentTarget) {
            return;
        }

        event.preventDefault();
    };

    const onKeyUp = (event: OptionRowKeyboardEventType) => {
        if(event.key !== OPTION_ROW_SPACE_KEY || event.target !== event.currentTarget || !onPress) {
            return;
        }

        event.preventDefault();

        onPress();
    };

    const describedBy = [
        ariaDescribedBy,
        isDescriptionVisible ? descriptionId : undefined
    ].filter((id) => !!id).join(" ");

    const webProps: OptionRowWebPropsType = isWeb ? {
        "aria-describedby": describedBy.length ? describedBy : undefined,
        onMouseDown: isFocusPreserved ? preventFocusSteal : undefined,
        onKeyDown: isSpaceKeyHandled ? onKeyDown : undefined,
        onKeyUp: isSpaceKeyHandled ? onKeyUp : undefined,
        dataSet: isDisabled ? {
            [INTERACTION_STATE_DATA_KEY]: "disabled"
        } : interactionHandlers.dataSet
    } : {};

    const roleProps = {
        role
    } as Pick<PressableProps, "role">;

    const isCheckedRole = role === "menuitemcheckbox" || role === "menuitemradio";

    const renderCheckBox = () => {
        if(!isMultiple) {
            return null;
        }

        return <View
            style={[
                stylesheet.iconSlot,
                iconSlotDynamicStyle
            ]}
            aria-hidden={true}
        >
            <CheckBox
                accessibilityLabel={labelText}
                customTheme={customTheme}
                isDisabled={isDisabled}
                spreadBehaviour="free"
                isChecked={isSelected}
            />
        </View>;
    };

    const renderLeading = () => {
        if(leading) {
            return <View
                style={stylesheet.leadingSlot}
            >
                {leading}
            </View>;
        }

        if(IconComponentProp) {
            return <View
                style={[
                    stylesheet.iconSlot,
                    iconSlotDynamicStyle
                ]}
                aria-hidden={true}
            >
                <IconComponentProp
                    customColor={currentColors.iconColor}
                    color={currentColors.iconColorKey}
                    size={currentMeasures.iconSize}
                />
            </View>;
        }

        if(!isReserveLeading || isMultiple) {
            return null;
        }

        return <View
            style={[
                stylesheet.iconSlot,
                iconSlotDynamicStyle
            ]}
            aria-hidden={true}
        />;
    };

    const renderLabel = () => {
        return <Text
            style={isDescriptionVisible ? undefined : stylesheet.label}
            customColor={currentColors.labelColor}
            variant={currentLabelVariant}
            customTheme={customTheme}
            numberOfLines={1}
        >
            {label}
        </Text>;
    };

    const renderText = () => {
        if(!isDescriptionVisible) {
            return renderLabel();
        }

        return <View
            style={[
                stylesheet.textColumn,
                textColumnDynamicStyle
            ]}
        >
            {renderLabel()}
            <Text
                customColor={currentColors.descriptionColor}
                variant={OPTION_ROW_DESCRIPTION_VARIANT}
                customTheme={customTheme}
                id={descriptionId}
                numberOfLines={2}
            >
                {description}
            </Text>
        </View>;
    };

    const renderMeta = () => {
        if(!isMetaVisible) {
            return null;
        }

        return <Text
            customColor={currentColors.metaColor}
            variant={OPTION_ROW_META_VARIANT}
            customTheme={customTheme}
            style={stylesheet.meta}
            numberOfLines={1}
        >
            {meta}
        </Text>;
    };

    const renderShortcut = () => {
        if(!shortcut || variant === "sheet") {
            return null;
        }

        return <Text
            customColor={currentColors.shortcutColor}
            variant={OPTION_ROW_SHORTCUT_VARIANT}
            customTheme={customTheme}
            style={[
                stylesheet.shortcut,
                shortcutDynamicStyle
            ]}
            numberOfLines={1}
        >
            {shortcut}
        </Text>;
    };

    const renderTrailing = () => {
        if(!trailing) {
            return null;
        }

        return <View
            style={[
                stylesheet.trailing,
                trailingDynamicStyle
            ]}
        >
            {trailing}
        </View>;
    };

    const renderCheck = () => {
        if(!isShowCheck) {
            return null;
        }

        return <View
            style={[
                stylesheet.iconSlot,
                iconSlotDynamicStyle
            ]}
            aria-hidden={true}
        >
            {
                isCheckRendered ?
                    <Animated.View
                        style={checkAppearStyle}
                    >
                        <CheckIcon
                            color={currentColors.checkColor}
                            size={currentMeasures.iconSize}
                        />
                    </Animated.View>
                :
                    null
            }
        </View>;
    };

    return <Pressable
        {...props}
        {...interactionHandlers}
        {...webProps}
        {...roleProps}
        aria-selected={ariaSelected ?? (role === "option" ? isSelected : undefined)}
        aria-checked={ariaChecked ?? (isCheckedRole ? isSelected : undefined)}
        onPress={isDisabled ? undefined : onPress}
        aria-disabled={isDisabled}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        disabled={isDisabled}
        tabIndex={tabIndex}
        ref={ref}
    >
        <StateLayer
            overlayColor={currentColors.overlayColor}
            borderRadius={currentRadius}
            isHovered={isLayerHovered}
            isPressed={isLayerPressed}
            customTheme={customTheme}
        />
        {renderCheckBox()}
        {renderLeading()}
        {renderText()}
        {renderMeta()}
        {renderShortcut()}
        {renderTrailing()}
        {renderCheck()}
    </Pressable>;
};
export default Object.assign(forwardRef(OptionRow), {
    Highlight: OptionRowHighlight
});
